/**
 * achFilesUi — the pure words and rules behind the ACH Files screen.
 *
 * Blocker labels: every flag the API emits gets a human label. The flags come
 * from achOrchestrator.eligibilityFor (its own, plus debitGate's spread in),
 * from collectionFileBlockers (debitPending, and since Amendment A4 the file
 * entry's binding and category flags beside every eligibility flag) and, under
 * the corporate Business PAD contract (2026-10-25), from the new account-kind,
 * PAD-binding, verification and signing checks. A key not listed here is
 * worded from its camelCase rather than shown raw.
 *
 * PAD category (I6): one ACH file holds one PAD category. BUSINESS files carry
 * CCD and the business client number, PERSONAL files PPD and the personal one,
 * and the API answers a mixed request with 409 MIXED_PAD_CATEGORIES. The
 * screen filters by category and sends a selection only when every row in it
 * carries the same known category (PERSONAL or BUSINESS): a row the API sent
 * with no category could be either, so it is never sent. The server enforces
 * the one-category rule either way.
 *
 * Refusals (Amendments A4.3 and A5, api 234d69d5) are worded by their code,
 * never with the server's text: 503 FLAG_STATE_UNKNOWN on every ACH call (a
 * flag that gates the debit could not be read), and 409
 * ACH_FILE_ENTRIES_BLOCKED on download and upload, whose per-entry blockers
 * are listed as the generate refusal lists its own.
 */
import { padCategoryLabel, resolvedOrderKind } from "../../../utils/orderAccountKind";
import { FLAG_STATE_UNKNOWN_TEXT, isFlagStateUnknown } from "../../../utils/flagState";

export const BLOCKER_LABELS = {
  // eligibilityFor
  padAuthorized: "PAD",
  // The PAD must be bound to the instrument version that is current now (I2):
  // a coordinate change supersedes it and a new PAD is needed.
  padBankMatches: "PAD bound to the current bank account",
  // ACH debits need a Canadian institution/transit/account; an investor who
  // saved an international (IBAN) account is not a debit-file candidate.
  bankDetailsComplete: "Bank details (CA account)",
  // ACH094 files are ASCII-only; a name with no representable characters
  // (A-Z, 0-9) after normalization cannot go on a debit record.
  achNameSafe: "Name (ACH charset)",
  signingSatisfied: "Documents",
  suitabilityAccepted: "Suitability",
  validAmount: "Amount",
  campaignCurrencyCad: "Campaign currency (CAD only)",
  // debitGate
  orderConfirmedLive: "Order confirmed and open",
  residencyRoutesAch: "Routed to ACH (not wire)",
  offeringReady: "Offering ready",
  termsReviewClear: "Fund terms review",
  // collectionFileBlockers
  debitPending: "Debit pending on this order",
  // corporate Business PAD contract (2026-10-25)
  accountKindResolved: "Account kind (individual or company)",
  padCategoryMatches: "PAD category matches the account",
  debitAccountVerified: "Company bank account verified",
  businessPadReady: "Business PAD collection enabled",
  allPartiesSigned: "All parties signed",
  // Amendment A4.1 / A4.2: the release blockers of a file entry
  // (achOrchestrator.entryBindingFlags). An entry binds the PAD and the
  // instrument version it was generated under; download and upload refuse it
  // when that binding moved, when it recorded none, or when its PAD category
  // is not the file's. Generation's own re-check answers fileBindingMatches.
  fileBindingMatches: "Bank or PAD changed after the file was generated; regenerate",
  fileBindingRecorded: "File generated before consent binding; regenerate",
  fileCategoryMatches: "PAD category does not match the file",
};

/** "someFutureFlag" -> "Some future flag". */
function words(key) {
  const spaced = String(key || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim()
    .toLowerCase();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : "Unknown check";
}

/**
 * Amendment A3 (2026-10-25): an ENTITY version with a NULL holder_name has no
 * payor, and the API answers achNameSafe false for it as for a name with no
 * representable characters (the remedy is WP9 --apply, which sets the legal
 * entity name). So on a resolved ENTITY row the label says that too. Every
 * other row, and every other key, keeps its plain label.
 */
export const ENTITY_ACH_NAME_LABEL = "Name (ACH charset), or the company name is missing on the bank record";

/** The label of one blocker key; `row` (the eligible row it belongs to, if known) words achNameSafe for an ENTITY order. */
export function blockerLabel(key, row) {
  if (key === "achNameSafe" && resolvedOrderKind(row).kind === "ENTITY") return ENTITY_ACH_NAME_LABEL;
  return Object.prototype.hasOwnProperty.call(BLOCKER_LABELS, key) ? BLOCKER_LABELS[key] : words(key);
}

const CATEGORIES = new Set(["PERSONAL", "BUSINESS"]);

/** PERSONAL | BUSINESS, or UNRESOLVED when the API sent no category. */
export function rowCategory(row) {
  const c = row && row.padCategory;
  return CATEGORIES.has(c) ? c : "UNRESOLVED";
}

export const CATEGORY_FILTERS = [
  { value: "", label: "All categories" },
  { value: "PERSONAL", label: "Personal PAD" },
  { value: "BUSINESS", label: "Business PAD" },
  { value: "UNRESOLVED", label: "Not resolved" },
];

export function filterByCategory(rows, filter) {
  const list = Array.isArray(rows) ? rows : [];
  if (!filter) return list;
  return list.filter((r) => rowCategory(r) === filter);
}

/** The distinct categories a selection of investment ids spans. */
export function categoriesOf(rows, ids) {
  const wanted = new Set((ids || []).map(Number));
  const found = new Set();
  for (const r of rows || []) {
    if (wanted.has(Number(r.investmentId))) found.add(rowCategory(r));
  }
  return [...found];
}

/**
 * What a selection of investment ids can be sent as: 'PERSONAL' or
 * 'BUSINESS' when every id is a row of that one known category, 'MIXED' when
 * it spans both, 'UNRESOLVED' when any id has no known category (or is no
 * longer in the list), null when nothing is selected. Only PERSONAL and
 * BUSINESS are sendable.
 */
export function selectionCategory(rows, ids) {
  const wanted = (ids || []).map(Number);
  if (!wanted.length) return null;
  const byId = new Map((rows || []).map((r) => [Number(r.investmentId), r]));
  const found = new Set();
  for (const id of wanted) {
    const category = byId.has(id) ? rowCategory(byId.get(id)) : "UNRESOLVED";
    if (category === "UNRESOLVED") return "UNRESOLVED";
    found.add(category);
  }
  return found.size === 1 ? [...found][0] : "MIXED";
}

export const UNRESOLVED_SELECTION_NOTE =
  "A selected investment has no PAD category from the server, so it cannot go in a debit file. Select only investments with a personal or business PAD category.";

export const ONE_CATEGORY_NOTE =
  "One ACH file holds one PAD category: generate personal and business debits as separate files.";

const isMixed = (b) => b.code === "MIXED_PAD_CATEGORIES" || b.message === "MIXED_PAD_CATEGORIES";

/**
 * "#id: label, label" for each blocked order: only the flags that are false,
 * each in words. A release refusal carries every eligibility flag of a failing
 * entry, true ones too. `rows` (the eligible list on the page) names each
 * order's kind for the achNameSafe wording; an order not in it (a release
 * entry is PENDING, so it is not) is worded plainly.
 */
function blockerLines(blockers, rows) {
  if (!blockers || typeof blockers !== "object") return [];
  const rowById = new Map((Array.isArray(rows) ? rows : []).map((r) => [String(r && r.investmentId), r]));
  return Object.entries(blockers).map(([id, flags]) => {
    const failed = Object.entries(flags || {})
      .filter(([, ok]) => !ok)
      .map(([k]) => blockerLabel(k, rowById.get(String(id))));
    return `#${id}: ${failed.length ? failed.join(", ") : "blocked"}`;
  });
}

/**
 * The generate-file refusal in words. `rows` (the eligible list on the page)
 * names each blocked order's kind: the refusal's blockers carry flags only.
 */
export function generateRefusalText(body, rows) {
  const b = body && typeof body === "object" ? body : {};
  if (isFlagStateUnknown(b)) return FLAG_STATE_UNKNOWN_TEXT;
  if (isMixed(b)) {
    const list = (Array.isArray(b.details) ? b.details : [])
      .map((d) => `#${d.investmentId} ${padCategoryLabel(d.padCategory)}`)
      .join(", ");
    return `A debit file holds one PAD category, and this selection mixes personal and business PADs${
      list ? ` (${list})` : ""
    }. Filter by category and generate one file per category. No file was generated.`;
  }
  if (b.code === "BUSINESS_PAD_PROFILE_NOT_CONFIGURED" || b.message === "BUSINESS_PAD_PROFILE_NOT_CONFIGURED") {
    return "The business PAD profile is not configured (no RBC business client number), so business debits cannot be collected yet. No file was generated.";
  }
  const message = typeof b.message === "string" && b.message.trim() ? b.message.trim() : "";
  if (b.blockers && typeof b.blockers === "object") {
    return `${message || "Some investments are not eligible."} Blocked investments: ${blockerLines(b.blockers, rows).join("; ")}`;
  }
  return message || "The server gave no reason.";
}

/** The eligible list's refusal in words, or null to keep the server's message. */
export function loadRefusalText(body) {
  return isFlagStateUnknown(body) ? FLAG_STATE_UNKNOWN_TEXT : null;
}

const FILE_VERBS = {
  download: "downloaded",
  upload: "marked as uploaded to RBC",
  status: "moved to that status",
};

/**
 * A download or status-change refusal of `file` in words, or null when the
 * caller keeps its own message. `action` is 'download', 'upload' (GENERATED ->
 * UPLOADED_TO_RBC) or 'status' (any other move).
 *   FLAG_STATE_UNKNOWN        a flag that gates the debit could not be read;
 *   ACH_FILE_ENTRIES_BLOCKED  an entry can no longer be debited (its order,
 *                             its consent binding, the file's PAD category or
 *                             any eligibility flag): each blocked entry and
 *                             its false flags, then what to do.
 */
export function fileRefusalText(body, file, action, rows) {
  const b = body && typeof body === "object" ? body : null;
  if (!b) return null;
  if (isFlagStateUnknown(b)) return FLAG_STATE_UNKNOWN_TEXT;
  if (b.code === "ACH_FILE_ENTRIES_BLOCKED") {
    const name = file && file.file_name ? `File ${file.file_name}` : "This file";
    const verb = FILE_VERBS[action] || FILE_VERBS.status;
    const lines = blockerLines(b.blockers, rows);
    const which = lines.length
      ? `these orders can no longer be debited. Blocked investments: ${lines.join("; ")}.`
      : "some of its orders can no longer be debited.";
    return `${name} cannot be ${verb}: ${which} Void the file and generate a new one for the orders that are still eligible. Nothing was changed.`;
  }
  return null;
}

function parseObject(text) {
  try {
    const value = JSON.parse(text);
    return value && typeof value === "object" ? value : null;
  } catch (_) {
    return null;
  }
}

/**
 * An error body as an object, or null. The download asks for a Blob
 * (responseType 'blob'), so its error body is a Blob: it is read as text with
 * FileReader (jsdom has no Blob.prototype.text) and parsed as JSON. An object
 * passes through; a string is parsed. Never throws.
 */
export async function readErrorBody(data) {
  try {
    if (data && typeof Blob !== "undefined" && data instanceof Blob) {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(data);
      });
      return parseObject(text);
    }
    if (typeof data === "string") return parseObject(data);
    if (data && typeof data === "object") return data;
    return null;
  } catch (_) {
    return null;
  }
}
